import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Collapse from '@mui/material/Collapse';
import useCheckEligibility from '../../apiHooks/useCheckEligibility';
import { Step1IneligibleMessage, Step1CTA1, TermsAndConditionsText } from './tenantComponents';
import { getTenantAssetsDir, getMicrositeTenantAssetsDir } from '../../utils';

/* eslint-disable max-len */

/**
 *
 * @param {object} props
 * @param {(enteredInfo: {streetAddress: string, lastName: string, zipCode: string}, eligibilityResponse: EHersCheckEligibilityResponse) => void} props.onSuccess
 * @returns {JSX.Element}
 */
// eslint-disable-next-line max-lines-per-function
const EHersEnrollmentStep1 = ({ onSuccess }) => {
  const sanitizeAddressInput = (value) => value.replace(/[^A-Za-z0-9 \-,.'#/()_@&]/g, '');
  const sanitizePersonNameInput = (value) => value.replace(/[^A-Za-z \-'.&]/g, '');
  const sanitizeZipInput = (value) => {
    const digitsAndHyphen = value.replace(/[^\d-]/g, '');
    const digitsOnly = digitsAndHyphen.replace(/-/g, '');

    if (digitsOnly.length > 5) {
      return `${digitsOnly.slice(0, 5)}-${digitsOnly.slice(5, 9)}`;
    }

    // Allow a trailing hyphen after exactly 5 digits so users can manually
    // type the ZIP+4 separator before entering the suffix digits.
    if (digitsOnly.length === 5 && digitsAndHyphen.includes('-')) {
      return `${digitsOnly}-`;
    }

    return digitsOnly.slice(0, 5);
  };

  const [alertIsClosed, setAlertIsClosed] = useState(false);
  const onCloseAlert = () => {
    setAlertIsClosed(true);
  };

  const currentYear = new Date().getFullYear();

  const {
    hostId,
    logoHostAssetName,
    ehersEnrollmentContent: {
      RIGHT_IMAGE_ASSET_NAME,
      BOTTOM_IMAGE_ASSET_NAME,
    },
  } = window.SE_CLIENT_CONFIG;

  const hostDomain = window.SE_HOST_DOMAIN;
  const micrositeId = window.SE_MICROSITE_ID;
  const assetsHostsDir = getTenantAssetsDir(hostDomain, hostId);
  const assetsMicrositeDir = getMicrositeTenantAssetsDir(hostDomain, micrositeId, hostId);

  const {
    // isError: checkEligibilityError,
    mutate: checkEligibilityMutate,
    isLoading: checkEligibilityLoading,
    isError: checkEligibilityIsError,
  } = useCheckEligibility(hostDomain);

  const onClickGetStarted = (evt) => {
    const form = document.getElementById('landing-form');

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    evt.preventDefault();
    setAlertIsClosed(false);
    const zipInput = document.getElementById('zip');
    const addressInput = document.getElementById('street_address');
    // const address2Input = document.getElementById('street_address2');
    const lastNameInput = document.getElementById('last_name');

    checkEligibilityMutate(
      {
        zipCode: zipInput.value,
        address: addressInput.value,
        // address2: address2Input.value,
        lastName: lastNameInput.value,
      },
      {
        onSuccess: (eligibilityResponse) => {
          // console.log('Eligibility check successful:', eligibilityResponse);
          onSuccess({
            streetAddress: addressInput.value,
            lastName: lastNameInput.value,
            zipCode: zipInput.value,
          }, eligibilityResponse);
          // Handle successful response (e.g., navigate to next step)
        },
        onError: () => {
          // console.error('Eligibility check failed:', error);
        },
      },
    );
  };

  return (
    <div style={{ position: 'relative' }}>
      <div id="landing-page" className="container">
        <div className="row">
          <div className="col-lg-10 col-lg-offset-1 col-md-10 col-md-offset-1 col-sm-12 col-xs-12">
            <div className="top-logo">
              <img src={`${assetsHostsDir}/${logoHostAssetName}`} alt="logo" className="top-logo-img" />
            </div>
            <div className="row info-row">
              <div id="content-container" className="container-fluid">
                <div className="row">
                  <div id="left-container" className="col-lg-8 col-md-8 col-sm-12 col-xs-12">
                    <div className="left-container-copy">
                      <h1 className="tag-line">Home Energy Reports</h1>
                      <Step1CTA1 hostId={hostId} />
                    </div>
                    <div id="landing-form-container">
                      <form method="POST" id="landing-form">
                        <div className="row">
                          <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                            <div className="form-group ">
                              <input
                                id="street_address"
                                className="form-control input-sm"
                                name="street_address"
                                placeholder="Street Address*"
                                required
                                pattern="^[A-Za-z0-9 \-,.'#/()_@&]+$"
                                title="Only letters, numbers, spaces, and common address characters (-,.'#/()_@&) are allowed"
                                onInput={(evt) => {
                                  evt.currentTarget.value = sanitizeAddressInput(evt.currentTarget.value);
                                }}
                              />
                              <label htmlFor="street_address" style={{fontWeight: '300'}}><small className="text-dark">e.g. “123 Main St.” </small></label>
                            </div>
                            <div className="form-group ">
                              <input
                                id="zip"
                                type="text"
                                className="form-control input-sm"
                                name="zip"
                                placeholder="Zip Code*"
                                required
                                pattern="^\d{5}(?:-\d{4})?$"
                                maxLength="10"
                                title="Enter a valid ZIP code (12345 or 12345-6789)"
                                onInput={(evt) => {
                                  const enteredValue = evt.currentTarget.value;
                                  const sanitizedValue = sanitizeZipInput(enteredValue);
                                  evt.currentTarget.value = sanitizedValue;
                                  evt.currentTarget.setCustomValidity(
                                    sanitizedValue !== enteredValue
                                      ? 'Use numbers and an optional hyphen only (e.g. 12345 or 12345-6789).'
                                      : '',
                                  );
                                  if (sanitizedValue !== enteredValue) {
                                    evt.currentTarget.reportValidity();
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                            <div className="form-group ">
                              <input
                                id="last_name"
                                type="text"
                                className="form-control input-sm"
                                name="last_name"
                                placeholder="Last Name*"
                                required
                                pattern="^[A-Za-z \-'.&]+$"
                                title="Only letters, spaces, hyphens, apostrophes, periods, and ampersands are allowed"
                                onInput={(evt) => {
                                  evt.currentTarget.value = sanitizePersonNameInput(evt.currentTarget.value);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <Collapse
                          style={{
                            marginBottom: checkEligibilityIsError && !alertIsClosed ? 25 : 0,
                          }}
                          in={checkEligibilityIsError && !alertIsClosed}
                        >
                          <Step1IneligibleMessage
                            hostId={hostId}
                            onClose={onCloseAlert}
                          />
                        </Collapse>
                        <div className="form-group">
                          <button disabled={checkEligibilityLoading} type="submit" className="btn btn-block btn-green" onClick={onClickGetStarted}>GET STARTED</button>
                        </div>
                      </form>
                    </div>
                  </div>
                  <div id="right-container" className="col-lg-4 col-md-4 col-sm-12 col-xs-12">
                    <div className="image-bar">
                      <img src={`${assetsMicrositeDir}/${RIGHT_IMAGE_ASSET_NAME}`} alt="eHers Portal" className="img-responsive" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row" />
            <div className="row benefits-row">
              <img style={{ width: '100%' }} src={`${assetsMicrositeDir}/${BOTTOM_IMAGE_ASSET_NAME}`} alt="Know your power" />
            </div>
            <div className="row footer-row">
              <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                <div id="footer-container" className="text-center">
                  <TermsAndConditionsText hostId={hostId} currentYear={currentYear} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

EHersEnrollmentStep1.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};

export default EHersEnrollmentStep1;
